// Desktop Modal Scroll Fix
// Scrolls the desktop modal to the currently selected image based on URL matching
// This solution works with the default Shopify modal without modifying its structure

class DesktopModalScrollFix {
  constructor() {
    this.currentImageUrl = null;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupImageTracking();
        this.setupModalListener();
      });
    } else {
      this.setupImageTracking();
      this.setupModalListener();
    }
  }

  setupImageTracking() {
    // Track the currently displayed main image
    this.trackCurrentImage();
    
    // Listen for media gallery changes
    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const galleryViewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      if (galleryViewer) {
        galleryViewer.addEventListener('slideChanged', (event) => {
          this.trackCurrentImage();
        });
      }
    }

    // Listen for thumbnail clicks
    const thumbnails = document.querySelectorAll('[data-target]');
    thumbnails.forEach(thumbnail => {
      const button = thumbnail.querySelector('button');
      if (button) {
        button.addEventListener('click', () => {
          // Small delay to allow image to update
          setTimeout(() => {
            this.trackCurrentImage();
          }, 100);
        });
      }
    });
  }

  trackCurrentImage() {
    // Find the currently active main image
    const activeMedia = document.querySelector('.product__media-item.is-active');
    if (activeMedia) {
      const img = activeMedia.querySelector('img');
      if (img) {
        this.currentImageUrl = img.src;
      }
    }
  }

  setupModalListener() {
    // Listen for modal opening
    const modal = document.querySelector('product-modal');
    if (modal) {
      // Override the show method to add our scroll logic
      const originalShow = modal.show.bind(modal);
      modal.show = (opener) => {
        originalShow(opener);
        // Small delay to ensure modal content is rendered
        setTimeout(() => {
          this.scrollToCurrentImage();
        }, 100);
      };
    }
  }

  scrollToCurrentImage() {
    if (!this.currentImageUrl) {
      return;
    }

    const modal = document.querySelector('product-modal');
    if (!modal) {
      return;
    }

   

    // Find all images in the modal
    const modalImages = modal.querySelectorAll('img');

    let targetImage = null;
    let targetMediaItem = null;

    // Try to find matching image by URL
    modalImages.forEach((img, index) => {
      if (img.src === this.currentImageUrl) {
        targetImage = img;
        return;
      }
    });

    // If exact match not found, try to find by URL pattern (without size parameters)
    if (!targetImage) {
      const baseUrl = this.extractBaseUrl(this.currentImageUrl);
   
      
      modalImages.forEach((img, index) => {
        const imgBaseUrl = this.extractBaseUrl(img.src);
        if (imgBaseUrl === baseUrl) {
          targetImage = img;
          return;
        }
      });
    }

    if (targetImage) {
      // Find the parent media item
      targetMediaItem = targetImage.closest('[data-media-id]');
      if (targetMediaItem) {
        // Try a simpler approach - just use scrollIntoView with offset
        this.simpleScrollToElement(targetMediaItem);
      } 
    } 
  }

  simpleScrollToElement(element) {
    
    // Method 1: Direct scrollIntoView with offset
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start',
      inline: 'start'
    });
    
    // Method 2: Add some offset by scrolling the parent container
    setTimeout(() => {
      const parentContainer = element.closest('[role="document"]') || element.closest('.product-media-modal__content');
      if (parentContainer) {
        
        // Try to scroll up a bit
        const currentScroll = parentContainer.scrollTop || parentContainer.scrollLeft;
        const offset = 0; // pixels to scroll up (reduced from 200)
        
        if (parentContainer.scrollTop !== undefined) {
          parentContainer.scrollTop = Math.max(0, currentScroll - offset);
        }
        
        if (parentContainer.scrollLeft !== undefined) {
          parentContainer.scrollLeft = Math.max(0, currentScroll - offset);
        }
      }
    }, 300);
  }

  scrollToElement(element, modal) {
    
    // Find the correct scrollable container
    const scrollableContainer = this.findScrollableContainer(modal);
    
    if (scrollableContainer) {
      // Method 1: Use the scrollable container
      this.scrollContainerToElement(scrollableContainer, element);
    } else {
      // Method 2: Try to make container scrollable and then scroll
      this.makeContainerScrollableAndScroll(modal, element);
    }
    
    // Method 3: Fallback - scrollIntoView with different options
    setTimeout(() => {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }, 200);
  }

  findScrollableContainer(modal) {
    // Look for various possible scrollable containers
    const possibleContainers = [
      modal.querySelector('[role="document"]'),
      modal.querySelector('.product-media-modal__content'),
      modal.querySelector('.product-media-modal__dialog'),
      modal.querySelector('.media-modal__content'),
      modal.querySelector('.slider'),
      modal.querySelector('[class*="slider"]'),
      modal.querySelector('[class*="scroll"]')
    ];

    for (const container of possibleContainers) {
      if (container && container.scrollWidth > container.clientWidth) {
        return container;
      }
    }

    // If no scrollable container found, try to find the one with the most content
    for (const container of possibleContainers) {
      if (container) {
       
        
        // If it has more content than visible, it might be scrollable
        if (container.scrollWidth > container.clientWidth || container.scrollHeight > container.clientHeight) {
          return container;
        }
      }
    }

    return null;
  }

  scrollContainerToElement(container, element) {
    
    // Calculate the position to scroll to
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Calculate scroll position to show content above the image
    // Scroll to show some content above the image (about 200px above)
    const offsetAbove = 200; // pixels to show above the image
    const scrollLeft = element.offsetLeft - (containerRect.width / 2) + (elementRect.width / 2) - offsetAbove;
    
    
    
    // Try smooth scrolling first
    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: 'smooth'
    });
    
    // Fallback: direct scroll after a delay
    setTimeout(() => {
      container.scrollLeft = Math.max(0, scrollLeft);
    }, 100);
  }

  makeContainerScrollableAndScroll(modal, element) {
    
    const container = modal.querySelector('[role="document"]');
    if (!container) return;
    
    // Temporarily make container scrollable
    const originalOverflow = container.style.overflow;
    const originalOverflowX = container.style.overflowX;
    
    container.style.overflow = 'auto';
    container.style.overflowX = 'auto';
    
    // Force a reflow
    container.offsetHeight;
    
    // Now try to scroll with offset above the image
    const elementOffset = element.offsetLeft;
    const containerWidth = container.clientWidth;
    const offsetAbove = 200; // pixels to show above the image
    const scrollPosition = elementOffset - (containerWidth / 2) - offsetAbove;
    container.scrollLeft = Math.max(0, scrollPosition);
    
    // Restore original overflow after a delay
    setTimeout(() => {
      container.style.overflow = originalOverflow;
      container.style.overflowX = originalOverflowX;
    }, 1000);
  }

  extractBaseUrl(url) {
    // Extract base URL without size parameters
    // e.g., "image.jpg?width=1445" -> "image.jpg"
    return url.split('?')[0];
  }
}

// Initialize the desktop modal scroll fix
if (!window.DesktopModalScrollFix) {
  window.DesktopModalScrollFix = new DesktopModalScrollFix();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DesktopModalScrollFix;
}
